import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ContractContextNavigation from '../../components/ui/ContractContextNavigation';
import MilestoneTimeline from './components/MilestoneTimeline';
import MilestoneForm from './components/MilestoneForm';
import MilestoneTemplates from './components/MilestoneTemplates';
import BulkOperations from './components/BulkOperations';
import DependencyMapping from './components/DependencyMapping';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const MilestoneManagement = () => {
  const location = useLocation();
  const [contract] = useState({
    id: "CT-2024-001",
    title: "Master Service Agreement - TechCorp Solutions",
    status: "Active"
  });

  const [milestones, setMilestones] = useState([
    {
      id: 'ms-001',
      title: 'Project Kickoff Meeting',
      description: 'Initial stakeholder meeting to establish project scope and requirements',
      dueDate: '2024-02-15',
      status: 'completed',
      assignedParty: 'TechCorp Solutions',
      priority: 'high',
      notificationEnabled: true,
      reminderDays: 3,
      progress: 100,
      dependencies: [],
      completedDate: '2024-02-14'
    },
    {
      id: 'ms-002',
      title: 'Requirements Documentation',
      description: 'Complete technical and business requirements documentation',
      dueDate: '2024-03-01',
      status: 'completed',
      assignedParty: 'Innovate Services LLC',
      priority: 'high',
      notificationEnabled: true,
      reminderDays: 5,
      progress: 100,
      dependencies: ['ms-001'],
      completedDate: '2024-02-28'
    },
    {
      id: 'ms-003',
      title: 'System Architecture Design',
      description: 'Design system architecture and technical specifications',
      dueDate: '2024-03-15',
      status: 'in-progress',
      assignedParty: 'Innovate Services LLC',
      priority: 'high',
      notificationEnabled: true,
      reminderDays: 7,
      progress: 75,
      dependencies: ['ms-002']
    },
    {
      id: 'ms-004',
      title: 'Development Phase 1',
      description: 'Core system development and initial feature implementation',
      dueDate: '2024-04-30',
      status: 'upcoming',
      assignedParty: 'Innovate Services LLC',
      priority: 'medium',
      notificationEnabled: true,
      reminderDays: 14,
      progress: 0,
      dependencies: ['ms-003']
    },
    {
      id: 'ms-005',
      title: 'User Acceptance Testing',
      description: 'Comprehensive testing with client stakeholders',
      dueDate: '2024-05-15',
      status: 'upcoming',
      assignedParty: 'TechCorp Solutions',
      priority: 'high',
      notificationEnabled: true,
      reminderDays: 7,
      progress: 0,
      dependencies: ['ms-004']
    },
    {
      id: 'ms-006',
      title: 'Go-Live Preparation',
      description: 'Final deployment preparations and production setup',
      dueDate: '2024-06-01',
      status: 'overdue',
      assignedParty: 'Both Parties',
      priority: 'critical',
      notificationEnabled: true,
      reminderDays: 3,
      progress: 30,
      dependencies: ['ms-005']
    }
  ]);

  const [selectedMilestones, setSelectedMilestones] = useState([]);
  const [activeView, setActiveView] = useState('timeline'); // timeline, form, templates, dependencies
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileTab, setMobileTab] = useState('timeline');

  // Check for mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const handleAddMilestone = () => {
    setEditingMilestone(null);
    setActiveView('form');
    if (isMobileView) setMobileTab('form');
  };

  const handleEditMilestone = (milestone) => {
    setEditingMilestone(milestone);
    setActiveView('form');
    if (isMobileView) setMobileTab('form');
  };

  const handleSaveMilestone = (milestoneData) => {
    if (editingMilestone) {
      // Update existing milestone
      setMilestones(prev => prev?.map(m => 
        m?.id === editingMilestone?.id 
          ? { ...milestoneData, id: editingMilestone?.id }
          : m
      ));
    } else {
      // Add new milestone
      const newMilestone = {
        ...milestoneData,
        id: `ms-${Date.now()}`,
        progress: 0,
        status: 'upcoming'
      };
      setMilestones(prev => [...prev, newMilestone]);
    }
    
    setEditingMilestone(null);
    setActiveView('timeline');
    if (isMobileView) setMobileTab('timeline');
  };

  const handleDeleteMilestone = (milestoneId) => {
    setMilestones(prev => prev?.filter(m => m?.id !== milestoneId));
    setSelectedMilestones(prev => prev?.filter(id => id !== milestoneId));
  };

  const handleMilestoneComplete = (milestoneId) => {
    setMilestones(prev => prev?.map(m => 
      m?.id === milestoneId 
        ? { ...m, status: 'completed', progress: 100, completedDate: new Date()?.toISOString()?.split('T')?.[0] }
        : m
    ));
  };

  const handleMilestoneReorder = (reorderedMilestones) => {
    setMilestones(reorderedMilestones);
  };

  const handleBulkAction = (action, milestoneIds) => {
    switch (action) {
      case 'delete':
        setMilestones(prev => prev?.filter(m => !milestoneIds?.includes(m?.id)));
        setSelectedMilestones([]);
        break;
      case 'complete':
        setMilestones(prev => prev?.map(m => 
          milestoneIds?.includes(m?.id) 
            ? { ...m, status: 'completed', progress: 100, completedDate: new Date()?.toISOString()?.split('T')?.[0] }
            : m
        ));
        setSelectedMilestones([]);
        break;
      case 'updatePriority':
        // Handle priority update
        break;
      default:
        break;
    }
  };

  const handleTemplateApply = (templateMilestones) => {
    const newMilestones = templateMilestones?.map((template, index) => ({
      ...template,
      id: `ms-${Date.now()}-${index}`,
      status: 'upcoming',
      progress: 0
    }));
    
    setMilestones(prev => [...prev, ...newMilestones]);
    setActiveView('timeline');
    if (isMobileView) setMobileTab('timeline');
  };

  const renderDesktopView = () => (
    <div className="flex h-full">
      {/* Left Panel - Timeline */}
      <div className="flex-1 border-r border-border">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Milestone Timeline</h3>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveView('templates')}
                iconName="Layout"
              >
                Templates
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActiveView('dependencies')}
                iconName="GitBranch"
              >
                Dependencies
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleAddMilestone}
                iconName="Plus"
              >
                Add Milestone
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeView === 'timeline' && (
            <>
              <BulkOperations
                selectedMilestones={selectedMilestones}
                onBulkAction={handleBulkAction}
                milestones={milestones}
              />
              <MilestoneTimeline
                milestones={milestones}
                selectedMilestones={selectedMilestones}
                onMilestoneSelect={setSelectedMilestones}
                onMilestoneEdit={handleEditMilestone}
                onMilestoneDelete={handleDeleteMilestone}
                onMilestoneComplete={handleMilestoneComplete}
                onMilestoneReorder={handleMilestoneReorder}
              />
            </>
          )}
          
          {activeView === 'templates' && (
            <MilestoneTemplates
              onTemplateApply={handleTemplateApply}
              onBack={() => setActiveView('timeline')}
            />
          )}
          
          {activeView === 'dependencies' && (
            <DependencyMapping
              milestones={milestones}
              onBack={() => setActiveView('timeline')}
            />
          )}
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-96 bg-muted/30">
        {activeView === 'form' ? (
          <MilestoneForm
            milestone={editingMilestone}
            onSave={handleSaveMilestone}
            onCancel={() => setActiveView('timeline')}
            contractParties={['TechCorp Solutions', 'Innovate Services LLC', 'Both Parties']}
          />
        ) : (
          <div className="p-6">
            <div className="text-center py-12">
              <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Milestone Details</h3>
              <p className="text-muted-foreground mb-4">
                Select a milestone from the timeline to view or edit its details.
              </p>
              <Button onClick={handleAddMilestone} iconName="Plus">
                Add New Milestone
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderMobileView = () => (
    <div className="flex flex-col h-full">
      {/* Mobile Tab Navigation */}
      <div className="border-b border-border bg-card">
        <div className="flex overflow-x-auto">
          {[
            { key: 'timeline', label: 'Timeline', icon: 'Calendar' },
            { key: 'form', label: 'Add/Edit', icon: 'Plus' },
            { key: 'templates', label: 'Templates', icon: 'Layout' },
            { key: 'dependencies', label: 'Dependencies', icon: 'GitBranch' }
          ]?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => setMobileTab(tab?.key)}
              className={`flex items-center space-x-2 px-4 py-3 border-b-2 whitespace-nowrap transition-legal-fast ${
                mobileTab === tab?.key
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span className="text-sm font-medium">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Content */}
      <div className="flex-1 overflow-y-auto">
        {mobileTab === 'timeline' && (
          <>
            <BulkOperations
              selectedMilestones={selectedMilestones}
              onBulkAction={handleBulkAction}
              milestones={milestones}
              isMobile={true}
            />
            <MilestoneTimeline
              milestones={milestones}
              selectedMilestones={selectedMilestones}
              onMilestoneSelect={setSelectedMilestones}
              onMilestoneEdit={handleEditMilestone}
              onMilestoneDelete={handleDeleteMilestone}
              onMilestoneComplete={handleMilestoneComplete}
              onMilestoneReorder={handleMilestoneReorder}
              isMobile={true}
            />
          </>
        )}
        
        {mobileTab === 'form' && (
          <MilestoneForm
            milestone={editingMilestone}
            onSave={handleSaveMilestone}
            onCancel={() => {
              setEditingMilestone(null);
              setMobileTab('timeline');
            }}
            contractParties={['TechCorp Solutions', 'Innovate Services LLC', 'Both Parties']}
            isMobile={true}
          />
        )}
        
        {mobileTab === 'templates' && (
          <MilestoneTemplates
            onTemplateApply={handleTemplateApply}
            onBack={() => setMobileTab('timeline')}
            isMobile={true}
          />
        )}
        
        {mobileTab === 'dependencies' && (
          <DependencyMapping
            milestones={milestones}
            onBack={() => setMobileTab('timeline')}
            isMobile={true}
          />
        )}
      </div>

      {/* Mobile Floating Action Button */}
      {mobileTab === 'timeline' && (
        <div className="fixed bottom-6 right-6 z-40">
          <Button
            onClick={handleAddMilestone}
            size="lg"
            className="shadow-legal-lg rounded-full w-14 h-14"
            iconName="Plus"
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ContractContextNavigation 
        contractId={contract?.id}
        contractTitle={contract?.title}
      />
      <main className="pt-32">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Milestone Management</h1>
                <p className="text-muted-foreground mt-1">
                  Track contract progress through visual timeline management and deadline monitoring
                </p>
              </div>
              {!isMobileView && (
                <div className="flex items-center space-x-2">
                  <div className="text-sm text-muted-foreground">
                    {milestones?.filter(m => m?.status === 'completed')?.length} of {milestones?.length} completed
                  </div>
                  <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-success transition-all duration-300"
                      style={{ 
                        width: `${(milestones?.filter(m => m?.status === 'completed')?.length / milestones?.length) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="h-[calc(100vh-280px)]">
          {isMobileView ? renderMobileView() : renderDesktopView()}
        </div>
      </main>
    </div>
  );
};

export default MilestoneManagement;